import { X, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ValuesPanel({ onClose }) {
  const navigate = useNavigate();

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose}></div>

      {/* Sliding Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">

        {/* Top Spacer to clear navbar */}
        <div className="h-20 bg-white"></div>

        {/* Sticky Header */}
        <div className="sticky top-20 bg-white border-b border-gray-200 p-6 z-20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Heart className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Core Values</h2>
              </div>
              <p className="text-sm text-gray-600">The principles that guide everything we do</p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
              aria-label="Close panel"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">

          {/* Introduction */}
          <div>
            <p className="text-base text-gray-700 leading-relaxed">
              Our core values shape our culture and drive us towards our mission of democratising AI marketing. These aren't just words—they guide every decision we make.
            </p>
          </div>

          {/* Values */}
          <div className="space-y-4">

            <div className="border-l-4 border-blue-600 pl-4">
              <h4 className="text-base font-bold text-gray-900 mb-2">Innovation</h4>
              <p className="text-sm text-gray-600 mb-2">Pushing boundaries and embracing change in a rapidly evolving AI landscape.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Ship new features every two weeks</li>
                <li>• Dedicate 20% of time to experimental AI features</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h4 className="text-base font-bold text-gray-900 mb-2">Transparency</h4>
              <p className="text-sm text-gray-600 mb-2">Building trust through openness and honest communication.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Clear pricing with no hidden fees</li>
                <li>• Public roadmap showing what we're building</li>
                <li>• Honest communication when things go wrong</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h4 className="text-base font-bold text-gray-900 mb-2">Excellence</h4>
              <p className="text-sm text-gray-600 mb-2">Delivering exceptional quality in every aspect of our platform.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Rigorous testing before every launch</li>
                <li>• World-class support with &lt;2 hour response time</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h4 className="text-base font-bold text-gray-900 mb-2">Customer-Centricity</h4>
              <p className="text-sm text-gray-600 mb-2">Our customers' success is our success. We're building partnerships, not just software.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Regular customer interviews to understand needs</li>
                <li>• Feature prioritisation based on customer impact</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h4 className="text-base font-bold text-gray-900 mb-2">Growth Mindset</h4>
              <p className="text-sm text-gray-600 mb-2">Learning from challenges, embracing feedback, and continuously improving.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Regular retrospectives to learn from successes and failures</li>
                <li>• Data-driven decision making with continuous testing</li>
              </ul>
            </div>

          </div>

          {/* Metrics */}
          <div className="bg-gray-50 border border-gray-200 p-5 rounded-lg">
            <h3 className="text-base font-bold text-gray-900 mb-3">Our Commitments</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 mb-1">Customer Satisfaction</p>
                <p className="text-xl font-bold text-blue-600">4.8/5.0</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Support Response</p>
                <p className="text-xl font-bold text-blue-600">&lt;2 hours</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Feature Releases</p>
                <p className="text-xl font-bold text-blue-600">Bi-weekly</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Retention Rate</p>
                <p className="text-xl font-bold text-blue-600">92%</p>
              </div>
            </div>
          </div>

        </div>

        {/* CTA at Bottom */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
          <button
            onClick={() => navigate('/signup')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
          >
            Start Free Trial
          </button>
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
